import Cookies from 'js-cookie'
import sbjs from 'sourcebuster'

import type { GeoLocationData } from '@/types/geo'
import { logger } from '@/utils/logger'
import { getCountryNameFromCode, getGlobalRegion } from '@/utils/geo'
import { clearEnhancedUserData, identifyUser, trackEvent } from '@/utils/analytics'
import { initSourcebuster } from '@/utils/sourcebusterConfig'
import { LINKEDIN_CLICK_ID_COOKIE, getLinkedInClickId, resolveLinkedInPaid } from '@/utils/linkedinAttribution'
import { useEffect, useRef, useCallback, type MutableRefObject } from 'react'
import { createPhoneExtensionEnhancer } from '@/hooks/usePhoneExtensionEnhancer'

// Extend global Window interface for additional properties
declare global {
  interface Window {
    growsumo?: {
      data?: {
        partner_key?: string
      }
    }
  }
}

// Note: JQuery type is defined in global.d.ts for script-loaded jQuery
// Note: HubSpot form config type is defined in global.d.ts (hbspt.forms.create)

interface HubSpotFormMetaData {
  portalId?: string
  formId?: string
  target?: string
  [key: string]: unknown
}

interface UtmFormField {
  name: string
  value?: string
}

interface TrackingData {
  firstname?: string
  lastname?: string
  email?: string
  phone?: string
  company?: string
  companysize?: string
  jobtitle?: string
  partnership_type?: string
  number_of_customers?: string
  comments__c?: string
  website?: string
  country_dropdown?: string
  partner___channel_type_2?: string
  country?: string
  region?: string
  globalRegion?: string
  [key: string]: string | undefined
}

const normalizeEmail = (email?: string): string => email?.trim().toLowerCase() ?? ''
const normalizeFieldValue = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value
  if (value === null || value === undefined) return undefined
  return String(value)
}

const toJQueryForm = (form: unknown): JQuery | null => {
  if (!form) return null

  if (typeof (form as { find?: unknown }).find === 'function') {
    return form as JQuery
  }

  if (typeof window === 'undefined' || !window.jQuery) {
    return null
  }

  try {
    if (form instanceof Element || form instanceof Document) {
      return window.jQuery(form)
    }

    if (typeof form === 'string') {
      return window.jQuery(form)
    }

    const maybeElement = (form as { 0?: unknown })[0]
    if (maybeElement instanceof Element) {
      return window.jQuery(maybeElement)
    }
  } catch (error) {
    logger.warn('[HubSpot] Failed to normalize form object:', {
      error: error instanceof Error ? error.message : String(error),
    })
  }

  return null
}

const getFormFieldValue = (form: JQuery, selector: string): string | undefined => {
  try {
    return normalizeFieldValue(form.find(selector).val())
  } catch {
    return undefined
  }
}

const setFormFieldValue = (form: JQuery, selector: string, value: unknown): void => {
  try {
    const normalizedValue = normalizeFieldValue(value)
    if (normalizedValue !== undefined) {
      const field = form.find(selector)
      field.val(normalizedValue)
      field.trigger('change')
    }
  } catch {
    // Best-effort only; never fail form rendering for hidden field hydration.
  }
}

const MESSAGE_FIELD_SELECTORS = [
  'textarea[name="comments__c"]',
  'textarea[name="message"]',
  'input[name="message"]',
  'textarea[name="TICKET.content"]',
]

const prefillHubSpotMessage = (form: JQuery, message?: string): void => {
  const trimmed = message?.trim()
  if (!trimmed) return

  for (const selector of MESSAGE_FIELD_SELECTORS) {
    if (form.find(selector).length) {
      setFormFieldValue(form, selector, trimmed)
      return
    }
  }
}

const getCountryCodeFromLocale = (locale: string): string | undefined => {
  const normalizedLocale = locale.replace(/_/g, '-')
  if (typeof Intl !== 'undefined' && typeof Intl.Locale !== 'undefined') {
    try {
      const region = new Intl.Locale(normalizedLocale).region
      if (region && /^[A-Za-z]{2}$/.test(region)) {
        return region.toUpperCase()
      }
    } catch {
      // Fall through to a lightweight BCP 47 parse.
    }
  }

  const segments = normalizedLocale.split('-').slice(1)

  for (const segment of segments) {
    if (/^[A-Za-z]{2}$/.test(segment)) {
      return segment.toUpperCase()
    }
  }

  return undefined
}

const getNavigatorGeoLocation = (): GeoLocationData | undefined => {
  if (typeof navigator === 'undefined') return undefined

  const locales = [...(navigator.languages || []), navigator.language].filter(Boolean)

  for (const locale of locales) {
    const country = getCountryCodeFromLocale(locale)
    if (!country) continue

    return {
      country,
      globalRegion: getGlobalRegion(country),
    }
  }

  return undefined
}

const VALID_GLOBAL_REGIONS = new Set<NonNullable<GeoLocationData['globalRegion']>>([
  'US/CANADA',
  'EMEA',
  'LATAM',
  'APAC',
])

const getValidatedGlobalRegion = (value?: string): GeoLocationData['globalRegion'] | undefined => {
  if (!value || !VALID_GLOBAL_REGIONS.has(value as NonNullable<GeoLocationData['globalRegion']>)) {
    return undefined
  }

  return value as GeoLocationData['globalRegion']
}

const getBodyGeoLocation = (): GeoLocationData | undefined => {
  if (typeof document === 'undefined') return undefined

  const { geoCountry, geoRegion, geoCity, geoGlobalRegion } = document.body.dataset

  if (!geoCountry && !geoRegion && !geoCity && !geoGlobalRegion) {
    return getNavigatorGeoLocation()
  }

  return {
    country: geoCountry || undefined,
    region: geoRegion || undefined,
    city: geoCity || undefined,
    globalRegion: getValidatedGlobalRegion(geoGlobalRegion),
  }
}

const normalizeOptionValue = (value: string): string => value.trim().toLowerCase()

const prefillCountryField = (form: JQuery, geoLocation?: GeoLocationData): void => {
  const countryCode = geoLocation?.country?.toUpperCase()
  if (!countryCode) return

  const countryName = getCountryNameFromCode(countryCode)
  const selectors = ['select[name="country_dropdown"]', 'select[name="country"]']

  selectors.forEach((selector) => {
    const field = form.find(selector)
    if (!field.length) return

    const currentValue = normalizeFieldValue(field.val())
    if (currentValue && currentValue.trim() !== '') return

    const selectElement = field[0]
    if (!(selectElement instanceof HTMLSelectElement)) return

    const options = Array.from(selectElement.options)
    const candidates = new Set(
      [countryCode, countryName]
        .filter((value): value is string => Boolean(value))
        .map((value) => normalizeOptionValue(value)),
    )

    const matchedOption = options.find((option) => {
      const normalizedValue = normalizeOptionValue(option.getAttribute('value') || '')
      const normalizedText = normalizeOptionValue(option.textContent || '')

      return candidates.has(normalizedValue) || candidates.has(normalizedText)
    })

    if (!matchedOption) return

    field.val(matchedOption.getAttribute('value') || matchedOption.textContent || '')
    field.trigger('change')
  })
}

const sanitizeIdToken = (value: string): string =>
  value
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .replace(/_+/g, '_')

const normalizeHubSpotFormLabelTargets = (form: JQuery): number => {
  const formElement = form?.[0]
  if (!(formElement instanceof HTMLFormElement)) return 0

  const labels = Array.from(formElement.querySelectorAll('label[for]'))
  if (!labels.length) return 0

  const usedIds = new Set(
    Array.from(formElement.querySelectorAll<HTMLElement>('[id]'))
      .map((element) => element.id)
      .filter(Boolean),
  )

  let fixedCount = 0

  labels.forEach((label, index) => {
    const htmlFor = label.getAttribute('for')
    if (!htmlFor) return

    // Already valid.
    const existingTarget = formElement.ownerDocument.getElementById(htmlFor)
    if (existingTarget && formElement.contains(existingTarget)) return

    const fieldContainer = label.closest('.hs-form-field, .hs-form-radio, .hs-form-checkbox') ?? label.parentElement
    const candidateInput =
      fieldContainer?.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
        'input:not([type="hidden"]), select, textarea',
      ) ?? null

    if (!candidateInput) return

    if (!candidateInput.id) {
      const fieldName = candidateInput.getAttribute('name') ?? `field_${index + 1}`
      const baseId = `hs-${sanitizeIdToken(fieldName)}`
      let candidateId = baseId
      let suffix = 1

      while (usedIds.has(candidateId)) {
        suffix += 1
        candidateId = `${baseId}-${suffix}`
      }

      candidateInput.id = candidateId
      usedIds.add(candidateId)
    }

    label.setAttribute('for', candidateInput.id)
    fixedCount += 1
  })

  return fixedCount
}

const scheduleHubSpotLabelNormalization = (form: JQuery, cleanupRef: MutableRefObject<(() => void)[]>): void => {
  // Keep runtime overhead minimal:
  // 1) normalize immediately after form mount
  // 2) run once more after async HubSpot/ChiliPiper field mutations
  normalizeHubSpotFormLabelTargets(form)

  const delayedNormalization = setTimeout(() => {
    normalizeHubSpotFormLabelTargets(form)
  }, 900)

  cleanupRef.current.push(() => clearTimeout(delayedNormalization))
}

const useHubSpotForm = (
  portalId: string | undefined,
  formId: string | undefined,
  sfdcCampaignId?: string,
  type?: string,
  geoLocation?: GeoLocationData | null,
  metaData?: HubSpotFormMetaData,
  target?: string,
  chiliPiperLoaded?: boolean,
  enableChiliPiper = true,
  uid?: string,
  scrollOnSuccess = true,
  prefillMessage?: string,
): void => {
  const isChiliPiperLoaded = chiliPiperLoaded ?? false
  const geoLocationRef = useRef<GeoLocationData | undefined>(geoLocation ?? getBodyGeoLocation())
  const prefillMessageRef = useRef(prefillMessage)
  const hubspotFormRef = useRef<JQuery | null>(null)
  const cleanupRef = useRef<(() => void)[]>([])

  useEffect(() => {
    initSourcebuster()
  }, [])

  useEffect(() => {
    geoLocationRef.current = geoLocation ?? getBodyGeoLocation()
  }, [geoLocation])

  useEffect(() => {
    prefillMessageRef.current = prefillMessage
    if (hubspotFormRef.current) {
      prefillHubSpotMessage(hubspotFormRef.current, prefillMessage)
    }
  }, [prefillMessage])

  // Cleanup function for intervals and event listeners
  const cleanup = useCallback(() => {
    cleanupRef.current.forEach((fn) => fn())
    cleanupRef.current = []
  }, [])

  const scrollToTop = useCallback(() => {
    // Mid-page embeds (e.g. the /cmmc-level-2 checklist) opt out so the
    // inline success message stays in view after submit.
    if (!scrollOnSuccess) return
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [scrollOnSuccess])

  const getUtmParamsFormFields = useCallback((): UtmFormField[] => {
    const current = sbjs?.get?.current
    const first = sbjs?.get?.first

    // Sourcebuster hardcodes fallback values for gclid-only traffic (no UTM
    // params in URL): campaign becomes "google_cpc" / "yandex_cpc", and
    // content/term become "(none)". These wrong values then sync to Salesforce
    // via the __c fields. Fix: prefer the URL param, then the first-visit
    // value (which HubSpot's Google Ads integration can correct), then fall
    // back to what Sourcebuster gave us.
    const SOURCEBUSTER_FALLBACKS = new Set(['google_cpc', 'yandex_cpc', '(none)'])
    const urlParams =
      typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()

    const resolveUtm = (
      currentVal: string | undefined,
      firstVal: string | undefined,
      urlParam: string,
    ): string | undefined => {
      const fromUrl = urlParams.get(urlParam)
      if (fromUrl) return fromUrl
      if (currentVal && !SOURCEBUSTER_FALLBACKS.has(currentVal)) return currentVal
      if (firstVal && !SOURCEBUSTER_FALLBACKS.has(firstVal)) return firstVal
      return currentVal
    }

    const linkedin = resolveLinkedInPaid({
      source: current?.src,
      medium: current?.mdm,
      campaign: current?.cmp,
      entrancePoint: sbjs?.get?.current_add?.ep,
      search: typeof window !== 'undefined' ? window.location.search : '',
    })

    return [
      { name: 'current_traffic_type', value: current?.typ },
      { name: 'utm_source__c', value: linkedin?.utm_source__c ?? current?.src },
      { name: 'utm_medium__c', value: linkedin?.utm_medium__c ?? current?.mdm },
      { name: 'utm_campaign__c', value: resolveUtm(current?.cmp, first?.cmp, 'utm_campaign') },
      { name: 'utm_content__c', value: resolveUtm(current?.cnt, first?.cnt, 'utm_content') },
      { name: 'utm_term__c', value: resolveUtm(current?.trm, first?.trm, 'utm_term') },
      { name: 'first_traffic_type', value: first?.typ },
      { name: 'first_utm_source', value: first?.src },
      { name: 'first_utm_medium', value: first?.mdm },
      { name: 'first_utm_campaign', value: first?.cmp },
      { name: 'first_utm_content', value: first?.cnt },
      { name: 'first_utm_term', value: first?.trm },
      { name: 'current_datetime', value: sbjs?.get?.current_add?.fd },
      { name: 'current_datetime__temp_', value: sbjs?.get?.current_add?.fd?.substring(0, 10) },
      { name: 'current_entrance_point__c', value: sbjs?.get?.current_add?.ep },
      { name: 'current_referrer_url', value: sbjs?.get?.current_add?.rf },
      { name: 'first_datetime', value: sbjs?.get?.first_add?.fd },
      { name: 'first_datetime__updated_', value: sbjs?.get?.first_add?.fd?.substring(0, 10) },
      { name: 'first_entrance_point__c', value: sbjs?.get?.first_add?.ep },
      { name: 'first_referrer_url', value: sbjs?.get?.first_add?.rf },
      { name: 'current_page_url', value: sbjs?.get?.session?.cpg },
    ]
  }, [])

  const handleFormReady = useCallback(
    (hubspotForm: unknown) => {
      const form = toJQueryForm(hubspotForm)
      if (!form) {
        logger.warn('[HubSpot] onFormReady received non-jQuery form object')
        return
      }

      hubspotFormRef.current = form

      scheduleHubSpotLabelNormalization(form, cleanupRef)

      // Initialize phone extension for phone field with flag icon
      // Wait for jQuery to be available before initializing
      const initializePhoneExtension = () => {
        try {
          // Check if form has the find method (jQuery is loaded)
          if (form && typeof form.find === 'function') {
            createPhoneExtensionEnhancer(form as any)
          } else {
            logger.warn('jQuery not available yet for phone extension enhancer')
          }
        } catch (error) {
          const err = error instanceof Error ? error : new Error(String(error))

          logger.error('PhoneExtensionDropdown initialization failed:', {
            message: err.message,
            name: err.name,
            stack: err.stack,
          })
        }
      }

      // If jQuery is available, initialize immediately
      if (typeof window !== 'undefined' && window.jQuery) {
        initializePhoneExtension()
      } else {
        // Otherwise, wait for jQuery to load (with timeout)
        let jqueryCheckInterval: NodeJS.Timeout | null = null
        let jqueryCheckTimeout: NodeJS.Timeout | null = null

        jqueryCheckInterval = setInterval(() => {
          if (window.jQuery && form && typeof form.find === 'function') {
            initializePhoneExtension()
            if (jqueryCheckInterval) {
              clearInterval(jqueryCheckInterval)
              jqueryCheckInterval = null
            }
            if (jqueryCheckTimeout) {
              clearTimeout(jqueryCheckTimeout)
              jqueryCheckTimeout = null
            }
          }
        }, 50)

        // Stop checking after 5 seconds
        jqueryCheckTimeout = setTimeout(() => {
          if (jqueryCheckInterval) {
            clearInterval(jqueryCheckInterval)
            jqueryCheckInterval = null
          }
          logger.warn('jQuery did not load in time for phone extension enhancer')
        }, 5000)

        // Store cleanup functions
        cleanupRef.current.push(() => {
          if (jqueryCheckInterval) {
            clearInterval(jqueryCheckInterval)
            jqueryCheckInterval = null
          }
          if (jqueryCheckTimeout) {
            clearTimeout(jqueryCheckTimeout)
            jqueryCheckTimeout = null
          }
        })
      }

      // Set form for Chili Piper form shortening functionality (only when enabled).
      if (enableChiliPiper && isChiliPiperLoaded && typeof window !== 'undefined' && window.ChiliPiper) {
        window.ChiliPiper.setForm(form)
      } else if (enableChiliPiper && typeof window !== 'undefined' && !isChiliPiperLoaded) {
        // Retry mechanism if ChiliPiper isn't loaded yet with proper cleanup
        let chiliPiperInterval: NodeJS.Timeout | null = null
        let chiliPiperTimeout: NodeJS.Timeout | null = null

        chiliPiperInterval = setInterval(() => {
          if (window.ChiliPiper) {
            window.ChiliPiper.setForm(form)
            if (chiliPiperInterval) {
              clearInterval(chiliPiperInterval)
              chiliPiperInterval = null
            }
            if (chiliPiperTimeout) {
              clearTimeout(chiliPiperTimeout)
              chiliPiperTimeout = null
            }
          }
        }, 100)

        // Clear interval after 10 seconds to prevent infinite checking
        chiliPiperTimeout = setTimeout(() => {
          if (chiliPiperInterval) {
            clearInterval(chiliPiperInterval)
            chiliPiperInterval = null
          }
          chiliPiperTimeout = null
        }, 10000)

        // Store cleanup functions
        cleanupRef.current.push(() => {
          if (chiliPiperInterval) {
            clearInterval(chiliPiperInterval)
            chiliPiperInterval = null
          }
          if (chiliPiperTimeout) {
            clearTimeout(chiliPiperTimeout)
            chiliPiperTimeout = null
          }
        })
      }

      if (type === 'demoEmbeddedForm') {
        const emailValue = typeof window !== 'undefined' && localStorage.getItem('user_email')
        const partnerKey = typeof window !== 'undefined' && window?.growsumo?.data?.partner_key

        setFormFieldValue(form, 'input[name="email"]', emailValue)
        setFormFieldValue(
          form,
          'input[name="partnerstack___referred_by_key"]',
          partnerKey || Cookies.get('growSumoPartnerKey'),
        )
      }

      prefillHubSpotMessage(form, prefillMessageRef.current)

      prefillCountryField(form, geoLocationRef.current)

      getUtmParamsFormFields().forEach((field) => {
        setFormFieldValue(form, `[name="${field.name}"]`, field.value)
      })

      const gclid = Cookies.get('gclid')
      if (gclid) {
        setFormFieldValue(form, 'input[name="gclid"]', gclid)
      }

      const linkedInClickId = getLinkedInClickId(window.location.search, Cookies.get(LINKEDIN_CLICK_ID_COOKIE))
      if (linkedInClickId) {
        setFormFieldValue(form, 'input[name="linkedin_click_id"]', linkedInClickId)
      }

      // OpenAI / ChatGPT Ads click ID (oppref) for offline conversion attribution
      // via the OpenAI Conversions API. Our GclidTracker captures the `oppref`
      // cookie; `__oppref` is the cookie the OpenAI pixel SDK sets as a fallback.
      const oppref = Cookies.get('oppref') || Cookies.get('__oppref')
      if (oppref) {
        setFormFieldValue(form, 'input[name="oppref"]', oppref)
      }

      // Meta Pixel _fbp cookie (browser ID) for Facebook CAPI match quality.
      // Click IDs (_fbc, gclid, li_fat_id, msclkid) are auto-captured by
      // the HubSpot Ads Integration into hs_* properties — no hidden fields needed.
      const fbp = Cookies.get('_fbp')
      if (fbp) {
        setFormFieldValue(form, 'input[name="fb_bid"]', fbp)
      }
    },
    [type, getUtmParamsFormFields, isChiliPiperLoaded, enableChiliPiper],
  )

  const handleFormSubmit = useCallback(
    (hubspotForm: unknown) => {
      const form = toJQueryForm(hubspotForm)
      if (!form) {
        logger.warn('[HubSpot] onFormSubmit received non-jQuery form object')
        scrollToTop()
        return
      }

      // Wrap analytics in try/catch so tracking failures never prevent
      // HubSpot from completing the form submission.
      try {
        const currentGeoLocation = geoLocationRef.current

        const trackingData: TrackingData = {
          firstname: getFormFieldValue(form, 'input[name="firstname"]'),
          lastname: getFormFieldValue(form, 'input[name="lastname"]'),
          email: getFormFieldValue(form, 'input[name="email"]'),
          phone: getFormFieldValue(form, 'input[name="phone"]'),
          company: getFormFieldValue(form, 'input[name="company"]'),
        }

        if (currentGeoLocation) {
          Object.assign(trackingData, {
            country: currentGeoLocation.country,
            region: currentGeoLocation.region,
            globalRegion: currentGeoLocation.globalRegion,
          })
        }

        // Add type-specific fields before identify so traits are complete.
        if (type === 'demoEmbeddedForm') {
          trackingData.companysize = getFormFieldValue(form, 'select[name="companysize"]')
        }

        if (type === 'contactPartnerEmbeddedForm') {
          Object.assign(trackingData, {
            jobtitle: getFormFieldValue(form, 'input[name="jobtitle"]'),
            partnership_type: getFormFieldValue(form, 'select[name="partnership_type"]'),
            number_of_customers: getFormFieldValue(form, 'input[name="number_of_customers"]'),
            comments__c: getFormFieldValue(form, 'textarea[name="comments__c"]'),
            website: getFormFieldValue(form, 'input[name="website"]'),
            country_dropdown: getFormFieldValue(form, 'select[name="country_dropdown"]'),
            partner___channel_type_2: getFormFieldValue(form, 'select[name="partner___channel_type_2"]'),
          })
        }

        const email = normalizeEmail(trackingData.email)
        if (email) {
          identifyUser(
            email,
            {
              ...trackingData,
              form_type: type ?? 'unknown',
            },
            {
              phone: trackingData.phone,
            },
          )
        } else {
          // No email captured on this submit — clear any prior session identifiers
          // so a later Meeting Booked can't inherit an earlier visitor's email (MAR-882).
          clearEnhancedUserData()
        }

        switch (type) {
          case 'resourceEmbeddedForm':
            trackEvent('Resource Download Clicked', trackingData)
            scrollToTop()
            break
          case 'demoEmbeddedForm':
            trackEvent('Demo Requested', trackingData)
            scrollToTop()
            break
          case 'contactPartnerEmbeddedForm':
            trackEvent(uid === 'summit' ? 'Event Registered' : 'Partner Demo Requested', trackingData)
            scrollToTop()
            break
          case 'ebookEmbeddedForm':
            trackEvent('Ebook Download Clicked', trackingData)
            scrollToTop()
            break
          case 'eventEmbeddedForm':
            trackEvent('Event Registered', trackingData)
            scrollToTop()
            break

          case 'opt-in-form':
          case 'email-only-form':
            trackEvent('Newsletter Subscribed', {
              ...trackingData,
              form_type: type,
            })
            break

          default:
            scrollToTop()
        }
      } catch (error) {
        logger.error('[Analytics] HubSpot form tracking failed:', {
          formType: type,
          error: error instanceof Error ? error.message : String(error),
        })
        // Still scroll to top even if analytics failed
        scrollToTop()
      }
    },
    [type, uid, scrollToTop],
  )

  useEffect(() => {
    if (!target || !portalId || !formId) return

    const scriptId = 'hubspot-forms-v2-script'
    let script = document.getElementById(scriptId) as HTMLScriptElement | null

    const createHubSpotForm = () => {
      if (window.hbspt && target && portalId && formId) {
        try {
          window.hbspt.forms.create({
            region: 'na1',
            portalId,
            formId,
            css: ' ',
            sfdcCampaignId,
            target: `#${target}`,
            onFormReady: handleFormReady,
            onFormSubmit: handleFormSubmit,
          })
        } catch (error) {
          const err = error instanceof Error ? error : new Error(String(error))

          logger.error('HubSpot form creation error:', { message: err.message, name: err.name, stack: err.stack })
        }
      }
    }

    const handleLoad = () => {
      createHubSpotForm()
    }

    const handleError = (error: Event) => {
      const err = error instanceof Error ? error : new Error(String(error))

      logger.error('HubSpot script loading error:', { message: err.message, name: err.name, stack: err.stack })
    }

    if (window.hbspt?.forms) {
      createHubSpotForm()
      return cleanup
    }

    if (!script) {
      script = document.createElement('script')
      script.id = scriptId
      script.src = '//js.hsforms.net/forms/embed/v2.js'
      script.async = true
      script.defer = true
      document.body.appendChild(script)
    }

    script.addEventListener('load', handleLoad)
    script.addEventListener('error', handleError)

    // Store cleanup functions for event listeners
    cleanupRef.current.push(
      () => script.removeEventListener('load', handleLoad),
      () => script.removeEventListener('error', handleError),
    )

    return cleanup
  }, [portalId, formId, sfdcCampaignId, target, handleFormReady, handleFormSubmit, cleanup])

  // Cleanup on unmount
  useEffect(() => {
    return cleanup
  }, [cleanup])
}

export default useHubSpotForm
